import { Stone } from '../types';

export interface StoneGroup {
  cod_piedra: string;
  nombre_piedra: string;
  type?: string;
  sizes: Map<number, Stone[]>;
  stoneIds: Map<number, number>; // Map<size, stone_id> para referencia rápida
}

/**
 * Agrupa piedras por nombre base (sin tamaño) y organiza por tamaños
 */
export function groupStonesByBaseName(stones: Stone[]): {
  groups: StoneGroup[];
  allSizes: number[];
} {
  const groupsMap = new Map<string, StoneGroup>();
  const allSizesSet = new Set<number>();

  stones.forEach((stone) => {
    // Extraer tamaño
    let size_mm: number | null = stone.size_mm ? parseFloat(stone.size_mm.toString()) : null;

    // Si no hay tamaño en size_mm, intentar extraerlo del código o nombre
    if (!size_mm || isNaN(size_mm) || size_mm === 0) {
      // Buscar patrón de tamaño en el código: -4mm, -6mm, etc.
      const codMatch = stone.cod_piedra.match(/-(\d+)mm$/i);
      if (codMatch) {
        size_mm = parseInt(codMatch[1], 10);
      } else {
        // Buscar patrón de tamaño en el nombre: " 4mm", " 6mm", etc.
        const nombreMatch = stone.nombre_piedra.match(/\s+(\d+)mm$/i);
        if (nombreMatch) {
          size_mm = parseInt(nombreMatch[1], 10);
        }
      }
    }

    // Redondear el tamaño al entero más cercano
    const effectiveSize = (size_mm && !isNaN(size_mm) && size_mm > 0) ? Math.round(size_mm) : 0;
    allSizesSet.add(effectiveSize);

    // Extraer el nombre base de la piedra (sin el tamaño)
    let baseCodPiedra = stone.cod_piedra;
    let baseNombrePiedra = stone.nombre_piedra;

    // Detectar si hay dimensiones múltiples en el código (ej: PIPE14X4, PIPE5X9)
    // Esto es importante para distinguir entre diferentes tipos de PIPE
    const multiDimMatch = baseCodPiedra.match(/(\d+)X(\d+)/i);
    let dimensionKey = '';
    if (multiDimMatch) {
      // Si hay dimensiones múltiples, incluirlas en la clave de agrupación
      dimensionKey = `${multiDimMatch[1]}X${multiDimMatch[2]}`;
    }

    // Eliminar el patrón de tamaño del código (ej: -4mm, -6mm, etc.)
    baseCodPiedra = baseCodPiedra.replace(/-\d+mm$/i, '');

    // Eliminar el patrón de tamaño del nombre (ej: " 4mm", " 6mm", etc.)
    baseNombrePiedra = baseNombrePiedra.replace(/\s+\d+mm$/i, '');

    // Si hay dimensiones múltiples, también eliminarlas del nombre base para limpieza
    // pero las mantenemos en la clave de agrupación
    if (dimensionKey) {
      baseNombrePiedra = baseNombrePiedra.replace(/\s*\d+X\d+\s*/i, '').trim();
    }

    // Usar el nombre base + dimensiones como clave de agrupación
    // Esto asegura que PIPE14X4 y PIPE5X9 sean grupos separados
    const key = dimensionKey 
      ? `${baseCodPiedra}-${baseNombrePiedra}-${dimensionKey}`
      : `${baseCodPiedra}-${baseNombrePiedra}`;

    if (!groupsMap.has(key)) {
      // Para el nombre mostrado, incluir las dimensiones si existen
      let displayNombre = baseNombrePiedra;
      if (dimensionKey) {
        // Agregar las dimensiones al nombre para distinguirlas visualmente
        displayNombre = `${baseNombrePiedra} ${dimensionKey}`;
      }
      
      groupsMap.set(key, {
        cod_piedra: baseCodPiedra,
        nombre_piedra: displayNombre,
        type: stone.type,
        sizes: new Map(),
        stoneIds: new Map(),
      });
    }

    const group = groupsMap.get(key)!;
    if (!group.sizes.has(effectiveSize)) {
      group.sizes.set(effectiveSize, []);
    }
    group.sizes.get(effectiveSize)!.push(stone);
    group.stoneIds.set(effectiveSize, stone.id);
  });

  // Ordenar tamaños: estándar primero (4, 6, 8, 10, 12), luego otros
  const standardSizes = [4, 6, 8, 10, 12];
  const otherSizes = Array.from(allSizesSet)
    .filter(s => !standardSizes.includes(s) && s !== 0)
    .sort((a, b) => a - b);
  const hasNoSize = allSizesSet.has(0);
  const sortedSizes = hasNoSize ? [0, ...standardSizes, ...otherSizes] : [...standardSizes, ...otherSizes];

  return {
    groups: Array.from(groupsMap.values()),
    allSizes: sortedSizes,
  };
}

/**
 * Filtra grupos de piedras por búsqueda
 */
export function filterStoneGroups(
  groups: StoneGroup[],
  searchTerm: string
): StoneGroup[] {
  if (!searchTerm.trim()) return groups;

  const search = searchTerm.toLowerCase().trim();
  return groups.filter(
    (group) =>
      group.cod_piedra.toLowerCase().includes(search) ||
      group.nombre_piedra.toLowerCase().includes(search)
  );
}

/**
 * Obtiene el tamaño de visualización para una columna
 */
export function getSizeLabel(size: number): string {
  return size === 0 ? 'Sin tamaño' : `${size}mm`;
}

